export type Effort = "low" | "medium" | "high" | "xhigh" | "max" | "ultra";
export type Decision = "start" | "caution" | "wait";

export type UsageSnapshot = {
  plan: "pro-20x";
  remainingPercent: number;
  reservePercent: number;
  activePeople: number;
  source: "dashboard" | "cli-status" | "estimate";
  note?: string;
  updatedAt?: string;
  updatedBy?: string;
};

export type ActiveSession = {
  id: string;
  userName: string;
  task: string;
  modelId: string;
  effort: Effort;
  decision: Decision;
  createdAt: string;
  durationMinutes: number;
  capacityPercent: number;
};

export type ModelInfo = {
  id: string;
  label: string;
  shortLabel: string;
  family: "5.6" | "5.5" | "5.4";
  posture: "frontier" | "balanced" | "efficient" | "legacy";
  bestFor: string;
  localMessages20x: [number, number];
  creditsPerMillion: {
    input: number;
    cachedInput: number;
    output: number;
  };
};

export type RecommendationInput = {
  task: string;
  usage: UsageSnapshot;
  activeSessions?: ActiveSession[];
  signals?: {
    urgent?: boolean;
    highValue?: boolean;
    productionRisk?: boolean;
    parallelizable?: boolean;
    needsPolish?: boolean;
  };
};

export type Recommendation = {
  decision: Decision;
  modelId: string;
  modelLabel: string;
  effort: Effort;
  effortLabel: string;
  headline: string;
  complexityScore: number;
  complexityLabel: string;
  effectiveAvailablePercent: number;
  estimatedDurationMinutes: number;
  estimatedCreditRange: [number, number];
  estimatedCapacityPercent: number;
  tags: string[];
  rationale: string[];
  alternatives: Array<{
    modelId: string;
    effort: Effort;
    reason: string;
  }>;
  waitReason?: string;
  assessor: "local" | "openai";
};

export const MODEL_LIMITS: ModelInfo[] = [
  {
    id: "gpt-5.6-sol",
    label: "GPT-5.6 Sol",
    shortLabel: "5.6 Sol",
    family: "5.6",
    posture: "frontier",
    bestFor: "Ambiguous, high-value coding, research, computer use, cybersecurity, and polished deliverables.",
    localMessages20x: [300, 1800],
    creditsPerMillion: { input: 125, cachedInput: 12.5, output: 750 }
  },
  {
    id: "gpt-5.6-terra",
    label: "GPT-5.6 Terra",
    shortLabel: "5.6 Terra",
    family: "5.6",
    posture: "balanced",
    bestFor: "Everyday engineering with strong reasoning and lower credit cost than Sol.",
    localMessages20x: [400, 2200],
    creditsPerMillion: { input: 62.5, cachedInput: 6.25, output: 375 }
  },
  {
    id: "gpt-5.6-luna",
    label: "GPT-5.6 Luna",
    shortLabel: "5.6 Luna",
    family: "5.6",
    posture: "efficient",
    bestFor: "Clear, repeatable tasks, extraction, transformation, structured summaries, and high-volume work.",
    localMessages20x: [1000, 5600],
    creditsPerMillion: { input: 25, cachedInput: 2.5, output: 150 }
  },
  {
    id: "gpt-5.5",
    label: "GPT-5.5",
    shortLabel: "5.5",
    family: "5.5",
    posture: "legacy",
    bestFor: "Previous-generation frontier work when compatibility with older prompts matters.",
    localMessages20x: [300, 1600],
    creditsPerMillion: { input: 125, cachedInput: 12.5, output: 750 }
  },
  {
    id: "gpt-5.4",
    label: "GPT-5.4",
    shortLabel: "5.4",
    family: "5.4",
    posture: "legacy",
    bestFor: "Lower-cost routine tasks when latest-model quality is not necessary.",
    localMessages20x: [400, 2000],
    creditsPerMillion: { input: 62.5, cachedInput: 6.25, output: 375 }
  },
  {
    id: "gpt-5.4-mini",
    label: "GPT-5.4 mini",
    shortLabel: "5.4 mini",
    family: "5.4",
    posture: "efficient",
    bestFor: "Very small, well-scoped, high-volume tasks.",
    localMessages20x: [1200, 7000],
    creditsPerMillion: { input: 18.75, cachedInput: 1.875, output: 113 }
  }
];

export const EFFORT_LABELS: Record<Effort, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  xhigh: "Extra high",
  max: "Max",
  ultra: "Ultra"
};

const EFFORT_MULTIPLIER: Record<Effort, number> = {
  low: 0.45,
  medium: 0.75,
  high: 1,
  xhigh: 1.35,
  max: 1.85,
  ultra: 2.8
};

const EFFORT_ORDER: Effort[] = ["low", "medium", "high", "xhigh", "max", "ultra"];

const DEFAULT_USAGE: UsageSnapshot = {
  plan: "pro-20x",
  remainingPercent: 70,
  reservePercent: 25,
  activePeople: 0,
  source: "estimate"
};

const tagPatterns: Array<{ tag: string; score: number; pattern: RegExp }> = [
  { tag: "new build", score: 18, pattern: /\b(build|create|make|ship|implement|launch)\b/i },
  { tag: "multi-file", score: 16, pattern: /\b(app|portal|dashboard|system|workflow|pipeline|full[- ]?stack|end[- ]?to[- ]?end)\b/i },
  { tag: "debugging", score: 12, pattern: /\b(debug|fix|broken|regression|failing|flaky|root cause)\b/i },
  { tag: "architecture", score: 18, pattern: /\b(architecture|redesign|migration|rewrite|refactor|scal(e|ing)|design system)\b/i },
  { tag: "production risk", score: 18, pattern: /\b(prod|production|deploy|database|auth|security|payment|billing|privacy|compliance|data loss)\b/i },
  { tag: "research", score: 12, pattern: /\b(research|compare|analyze|investigate|latest|current|verify|citation|source)\b/i },
  { tag: "frontend polish", score: 12, pattern: /\b(frontend|ui|ux|design|visual|landing|responsive|mobile|polish)\b/i },
  { tag: "parallel work", score: 12, pattern: /\b(multiple|several|bunch|batch|audit|inventory|many|subtasks|parallel|split)\b/i },
  { tag: "routine", score: -12, pattern: /\b(rename|typo|format|summarize|extract|classify|convert|copy edit|small change)\b/i },
  { tag: "well scoped", score: -8, pattern: /\b(single file|one file|quick|simple|small|minor|straightforward)\b/i }
];

export function defaultUsageSnapshot(): UsageSnapshot {
  return { ...DEFAULT_USAGE };
}

export function getModel(id: string): ModelInfo {
  return MODEL_LIMITS.find((model) => model.id === id) ?? MODEL_LIMITS[0];
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function recommend(input: RecommendationInput): Recommendation {
  const usage = sanitizeUsage(input.usage);
  const text = input.task.trim();
  const activeSessions = activeOnly(input.activeSessions ?? []);
  const taskProfile = scoreTask(text, input.signals);
  const sessionLoad = activeSessions.reduce((sum, session) => sum + session.capacityPercent, 0);
  const effectiveAvailable = clamp(
    usage.remainingPercent - usage.reservePercent - usage.activePeople * 6 - sessionLoad,
    0,
    100
  );

  let model = chooseModel(taskProfile, effectiveAvailable, input.signals);
  let effort = chooseEffort(taskProfile, effectiveAvailable, input.signals);
  let decision: Decision = "start";
  const rationale: string[] = [];
  let waitReason: string | undefined;

  if (!text) {
    decision = "wait";
    waitReason = "Add the task before reserving capacity.";
  } else if (effectiveAvailable < 8) {
    decision = "wait";
    waitReason = "The shared reserve is effectively exhausted.";
  } else if (effectiveAvailable < 18 && !input.signals?.urgent) {
    decision = "wait";
    waitReason = "Capacity is too low for non-urgent work.";
  } else if (effectiveAvailable < 32 && taskProfile.score >= 55 && !input.signals?.urgent) {
    decision = "wait";
    waitReason = "This is a complex task and the account is already under pressure.";
  } else if (effectiveAvailable < 40 || usage.activePeople + activeSessions.length >= 3) {
    decision = "caution";
  }

  if (effectiveAvailable < 35) {
    effort = capEffort(effort, "high");
    if (model.id === "gpt-5.6-sol" && taskProfile.score < 78) {
      model = getModel("gpt-5.6-terra");
    }
  }

  if (effectiveAvailable < 22 && input.signals?.urgent) {
    model = taskProfile.score >= 65 ? getModel("gpt-5.6-terra") : getModel("gpt-5.6-luna");
    effort = capEffort(effort, "medium");
    decision = "caution";
    rationale.push("Urgent work is allowed, but the recommendation is deliberately smaller.");
  }

  if (effort === "ultra" && !taskProfile.parallelizable) {
    effort = "max";
  }

  const duration = estimateDuration(taskProfile.score, effort);
  const credits = estimateCredits(model, effort, taskProfile.score);
  const estimatedCapacityPercent = clamp(Math.round((credits[1] / 40) * 8), 3, 28);

  rationale.push(...buildRationale(taskProfile, usage, effectiveAvailable, model, effort, decision));

  return {
    decision,
    modelId: model.id,
    modelLabel: model.label,
    effort,
    effortLabel: EFFORT_LABELS[effort],
    headline: headlineFor(decision, model, effort),
    complexityScore: taskProfile.score,
    complexityLabel: complexityLabel(taskProfile.score),
    effectiveAvailablePercent: Math.round(effectiveAvailable),
    estimatedDurationMinutes: duration,
    estimatedCreditRange: credits,
    estimatedCapacityPercent,
    tags: taskProfile.tags,
    rationale,
    alternatives: alternativesFor(model, effort, effectiveAvailable, taskProfile.score),
    waitReason,
    assessor: "local"
  };
}

export function activeOnly(sessions: ActiveSession[], now = Date.now()): ActiveSession[] {
  return sessions.filter((session) => {
    const created = Date.parse(session.createdAt);
    if (Number.isNaN(created)) return false;
    return created + session.durationMinutes * 60_000 > now;
  });
}

export function sanitizeUsage(usage?: Partial<UsageSnapshot>): UsageSnapshot {
  return {
    plan: "pro-20x",
    remainingPercent: clamp(Number(usage?.remainingPercent ?? DEFAULT_USAGE.remainingPercent), 0, 100),
    reservePercent: clamp(Number(usage?.reservePercent ?? DEFAULT_USAGE.reservePercent), 0, 80),
    activePeople: clamp(Math.round(Number(usage?.activePeople ?? DEFAULT_USAGE.activePeople)), 0, 20),
    source: usage?.source === "dashboard" || usage?.source === "cli-status" ? usage.source : "estimate",
    note: usage?.note?.slice(0, 240),
    updatedAt: usage?.updatedAt,
    updatedBy: usage?.updatedBy?.slice(0, 80)
  };
}

function scoreTask(text: string, signals?: RecommendationInput["signals"]) {
  let score = 26;
  const tags = new Set<string>();

  if (text.length > 220) score += 8;
  if (text.length > 700) score += 10;
  if (text.length > 1300) score += 8;

  for (const item of tagPatterns) {
    if (item.pattern.test(text)) {
      score += item.score;
      tags.add(item.tag);
    }
  }

  if (signals?.urgent) {
    score += 4;
    tags.add("urgent");
  }
  if (signals?.highValue) {
    score += 12;
    tags.add("high value");
  }
  if (signals?.productionRisk) {
    score += 16;
    tags.add("production risk");
  }
  if (signals?.parallelizable) {
    score += 9;
    tags.add("parallel work");
  }
  if (signals?.needsPolish) {
    score += 10;
    tags.add("frontend polish");
  }

  const parallelizable = tags.has("parallel work") || /\b(build|audit|migrate|rewrite|portfolio|suite|batch)\b/i.test(text);
  return {
    score: Math.round(clamp(score, 5, 100)),
    tags: Array.from(tags).slice(0, 7),
    parallelizable
  };
}

function chooseModel(
  profile: ReturnType<typeof scoreTask>,
  available: number,
  signals?: RecommendationInput["signals"]
): ModelInfo {
  if (available < 24 && profile.score < 62) return getModel("gpt-5.6-luna");
  if (available < 35 && profile.score < 78) return getModel("gpt-5.6-terra");
  if (profile.score >= 74 || signals?.highValue || signals?.needsPolish || signals?.productionRisk) {
    return getModel("gpt-5.6-sol");
  }
  if (profile.score <= 32 && profile.tags.some((tag) => tag === "routine" || tag === "well scoped")) {
    return getModel("gpt-5.6-luna");
  }
  return getModel("gpt-5.6-terra");
}

function chooseEffort(
  profile: ReturnType<typeof scoreTask>,
  available: number,
  signals?: RecommendationInput["signals"]
): Effort {
  const minimum: Effort =
    profile.score < 24 ? "low" : profile.score < 44 ? "medium" : profile.score < 64 ? "high" : profile.score < 82 ? "xhigh" : "max";
  let effort = generousEffort(minimum);

  if (profile.score >= 84 && profile.parallelizable && available >= 58) {
    effort = "ultra";
  }
  if (signals?.productionRisk && effortIndex(effort) < effortIndex("xhigh")) {
    effort = "xhigh";
  }
  if (available < 55) effort = capEffort(effort, "max");
  if (available < 40) effort = capEffort(effort, "xhigh");
  if (available < 28) effort = capEffort(effort, "high");
  return effort;
}

function generousEffort(effort: Effort): Effort {
  const next = EFFORT_ORDER[effortIndex(effort) + 1];
  return next ?? effort;
}

function capEffort(effort: Effort, cap: Effort): Effort {
  return effortIndex(effort) > effortIndex(cap) ? cap : effort;
}

function effortIndex(effort: Effort): number {
  return EFFORT_ORDER.indexOf(effort);
}

function estimateDuration(score: number, effort: Effort): number {
  const base = score < 30 ? 25 : score < 55 ? 55 : score < 78 ? 95 : 150;
  return Math.round(base * (EFFORT_MULTIPLIER[effort] + 0.35));
}

function estimateCredits(model: ModelInfo, effort: Effort, score: number): [number, number] {
  const modelBase = model.id.includes("luna") ? 5 : model.id.includes("terra") || model.id === "gpt-5.4" ? 8 : 12;
  const scope = score < 30 ? 0.75 : score < 55 ? 1 : score < 78 ? 1.6 : 2.25;
  const center = modelBase * scope * EFFORT_MULTIPLIER[effort];
  return [Math.max(3, Math.round(center * 0.65)), Math.max(6, Math.round(center * 1.45))];
}

function buildRationale(
  profile: ReturnType<typeof scoreTask>,
  usage: UsageSnapshot,
  available: number,
  model: ModelInfo,
  effort: Effort,
  decision: Decision
): string[] {
  const lines = [
    `${model.shortLabel} matches the task profile: ${model.bestFor}`,
    `${EFFORT_LABELS[effort]} is one step more generous than the minimum unless the shared account is tight.`,
    `Effective usable capacity is ${Math.round(available)}% after a ${usage.reservePercent}% reserve and active-user load.`
  ];

  if (profile.tags.length > 0) {
    lines.unshift(`Detected ${profile.tags.join(", ")}.`);
  }
  if (decision === "wait") {
    lines.push("Waiting protects the shared five-hour window for work already in progress.");
  }
  return lines;
}

function headlineFor(decision: Decision, model: ModelInfo, effort: Effort): string {
  if (decision === "wait") return "Wait before starting";
  if (decision === "caution") return `Start carefully on ${model.shortLabel} ${EFFORT_LABELS[effort]}`;
  return `Start on ${model.shortLabel} ${EFFORT_LABELS[effort]}`;
}

function complexityLabel(score: number): string {
  if (score >= 82) return "very complex";
  if (score >= 64) return "complex";
  if (score >= 44) return "moderate";
  if (score >= 24) return "light";
  return "routine";
}

function alternativesFor(model: ModelInfo, effort: Effort, available: number, score: number): Recommendation["alternatives"] {
  const alternatives: Recommendation["alternatives"] = [];
  if (model.id !== "gpt-5.6-luna" && available < 45) {
    alternatives.push({
      modelId: "gpt-5.6-luna",
      effort: capEffort(effort, "medium"),
      reason: "Use when preserving shared capacity matters more than polish."
    });
  }
  if (model.id !== "gpt-5.6-terra" && score < 78) {
    alternatives.push({
      modelId: "gpt-5.6-terra",
      effort: capEffort(effort, "high"),
      reason: "Balanced default for everyday engineering."
    });
  }
  if (model.id !== "gpt-5.6-sol" && available >= 55) {
    alternatives.push({
      modelId: "gpt-5.6-sol",
      effort: generousEffort(capEffort(effort, "xhigh")),
      reason: "Upgrade when quality and ambiguity matter more than usage."
    });
  }
  return alternatives.slice(0, 3);
}
